import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import type { RecurringOverviewResponse } from '@/lib/api';
import { monthKeyOf, occurrenceMonthKeyOf } from '@/lib/recurring/occurrence';

/**
 * Pure cycle model for the Assinaturas widget (W8) — kept out of the component
 * so the projection is testable without rendering.
 *
 * `paga` is reserved for account-debited charges (Pix/débito/boleto — money
 * actually left the account). A CARD-backed charge that landed is `na-fatura`:
 * it joined the open invoice and only gets paid with the fatura, so calling it
 * "paga" would lie. The groups already encode the split server-side —
 * `assinaturas` is card-bound by definition, `fixas` debits the account.
 */
export type AssinaturaCycleStatus = 'paga' | 'na-fatura' | 'prevista';

export interface AssinaturaCycleRow {
  id: string;
  name: string;
  categoryKey: string;
  iconName: string | null;
  status: AssinaturaCycleStatus;
  /** ISO charge date in the cycle: the charge date when landed, the expected date when `prevista`. */
  date: string | null;
  amount: number;
  /** What the charges that settled this cycle actually moved, when the server reported them and
   * the figure is comparable to `amount`. When set it is what the row renders and what the cycle
   * totals count, in place of the forecast. */
  settledAmount: number | null;
}

export interface AssinaturasCycle {
  rows: AssinaturaCycleRow[];
  /** The cycle's commitment: the server-computed `monthlyTotal`s, with each settled row's forecast
   * replaced by what it actually charged. Always equals `pagos + naFatura + previstos`. */
  total: number;
  /** Account-debited charges that landed — actually paid. */
  pagos: number;
  /** Card-backed charges that landed — on the open fatura, not paid yet. */
  naFatura: number;
  previstos: number;
}

function byCycleDate(a: AssinaturaCycleRow, b: AssinaturaCycleRow): number {
  if (!a.date) return b.date ? 1 : 0;
  if (!b.date) return -1;
  return a.date.localeCompare(b.date);
}

/** The recurring groups the Despesas Fixas card aggregates: subscriptions plus
 * fixed bills. Parcelamentos stay out — installments already live in Em aberto. */
const FIXED_EXPENSE_GROUPS = ['assinaturas', 'fixas'] as const;

/**
 * Projects the recurring overview into the widget's cycle view. An item is
 * `paga` when the server reports a linked charge settling this period — literally
 * the rows that make up `period.pago`, so a settlement is always backed by money
 * the aggregate counted. Absent one it falls back to attributing the series'
 * `lastSeen` to an occurrence month: a charge can land without ever reaching
 * `period.pago` (unlinked, or filtered out by the real-expense / account-profile
 * guards), and reading those as `prevista` would be the worse lie. So the chip is
 * a superset of the aggregate, not a mirror of it. Otherwise it's `prevista` for
 * its next expected date. Yearly items only participate in the month they bill.
 */
export function buildAssinaturasCycle(overview: RecurringOverviewResponse): AssinaturasCycle {
  const groups = overview.groups.filter((candidate) =>
    (FIXED_EXPENSE_GROUPS as readonly string[]).includes(candidate.key),
  );
  const periodKey = `${overview.period.year}-${String(overview.period.month).padStart(2, '0')}`;

  const rows: AssinaturaCycleRow[] = [];
  let settledDelta = 0;
  for (const group of groups) {
    for (const item of group.items) {
      // `assinaturas` items are card-bound by the server's own grouping rule —
      // but `fixas` is KIND-based (Utility/Pix), so a utility charged on a
      // credit card also sits on the open fatura: check the payment rail too
      // (same raw-Pluggy inference the transactions screen uses).
      const cardBacked =
        group.key === 'assinaturas' ||
        (item.paymentMethod ?? '').toLowerCase().includes('credit');
      const landedStatus: AssinaturaCycleStatus = cardBacked ? 'na-fatura' : 'paga';
      const settledAt = item.settledThisPeriodAt ?? null;
      const landedThisCycle =
        settledAt !== null ||
        occurrenceMonthKeyOf(item.lastSeen, item.dayOfMonth, item.cadence) === periodKey;
      const billsThisCycle =
        item.cadence !== 'Yearly' || landedThisCycle || monthKeyOf(item.nextExpectedDate) === periodKey;
      if (!billsThisCycle) continue;

      // A settlement replaces the row's forecast, so the cycle total moves by the difference.
      //
      // Weekly is excluded outright. Its cycle forecast is amount × weeks-per-month while `amount`
      // is one week and the settlement aggregates every charge in the period, so the three are
      // different quantities and no single row amount is right for all of them: the row would
      // either understate what was paid, declare the month finished after its first charge, or
      // let a five-charge month's overage eat into unrelated bills' forecasts. The status chip
      // still reads the settlement (see above) — only the money stays on the commitment, exactly
      // as it did before settlements existed. Representing a partially-settled weekly series needs
      // a row that can carry a paid and a pending part, which this model does not have.
      const settledAmount = item.cadence === 'Weekly' ? null : (item.settledThisPeriodAmount ?? null);
      if (settledAmount !== null) {
        // Net against what this series actually put into `monthlyTotal`, not against `amount`. They
        // differ for a yearly bill settled outside its anniversary month, which contributes nothing
        // to the forecast — netting against `amount` there would subtract a figure that was never
        // in the total and drag the cycle below what has been paid.
        settledDelta += settledAmount - (item.cycleForecastAmount ?? item.amount);
      }

      rows.push({
        id: item.id,
        name: item.merchantName ?? item.categoryTranslated ?? item.category ?? 'Despesa fixa',
        categoryKey: item.categoryId ?? item.category ?? 'outros',
        iconName: item.categoryIcon ?? item.parentCategoryIcon ?? null,
        status: landedThisCycle ? landedStatus : 'prevista',
        date: landedThisCycle ? (settledAt ?? item.lastSeen) : item.nextExpectedDate,
        amount: item.amount,
        settledAmount,
      });
    }
  }
  rows.sort(byCycleDate);

  // A settled row displays what actually left the account, so every total it feeds must use the
  // same number — otherwise the card shows the charge twice at two values. The cycle total stays
  // anchored on the server's forecast (it also carries weekly scaling and items with no row),
  // corrected by the settlement deltas accumulated above.
  const displayAmountOf = (row: AssinaturaCycleRow) => row.settledAmount ?? row.amount;
  const sumOf = (status: AssinaturaCycleStatus) =>
    rows.filter((row) => row.status === status).reduce((sum, row) => sum + displayAmountOf(row), 0);
  const pagos = sumOf('paga');
  const naFatura = sumOf('na-fatura');
  const forecastTotal = groups.reduce((sum, group) => sum + group.monthlyTotal, 0);
  const total = forecastTotal + settledDelta;
  // Every settled row swapped its forecast for its settlement in BOTH `total` and the subtotal
  // above, so the residual is the untouched forecast of the rows nobody has paid — non-negative by
  // construction. The clamp only absorbs float noise.
  return { rows, total, pagos, naFatura, previstos: Math.max(total - pagos - naFatura, 0) };
}

export function cycleDateLabel(date: string | null): string | null {
  if (!date) return null;
  // Parse the date-only part as a LOCAL date so a midnight-UTC value can't slip a
  // day, then render "15 jul" (day + short month, no trailing period).
  const [year, month, day] = date.slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  return format(new Date(year, month - 1, day), 'dd MMM', { locale: ptBR }).replace(/\.$/, '');
}
